import { useTranslation } from 'react-i18next';
import { toast } from "react-toastify";
import { useState } from "react";
import copy from "copy-to-clipboard";
/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer - IBL
 * @Email : kpozos@ibl.mx
 * CopySecretApp
 *
 * A utility hook component that handles copying a secret key and a generated password
 * to the clipboard. It also provides methods for UI state management and user feedback.
 *
 * @param {string} secret - The secret key to be copied.
 * @param {Function} setSecret - Function to reset the secret key.
 * @param {Function} setShow - Function to toggle visibility of a modal or component.
 * @param {Function} setData - Function to update external state, such as clearing a password.
 * @param {string} closeText - Text to display on the button after copying is complete.
 * @param {string} generatedPassword - The password to be copied along with the secret.
 * @param {Function} onHide - Callback function to be executed when hiding the component/modal.
 *
 * @returns {{
 *   t: Function,
 *   buttonText: string,
 *   handleConfirm: Function,
 *   handleConfirmGeneratedPassword: Function,
 *   onHideModal: Function
 * }}
 */

const CopySecretApp = ({ secret, setSecret, setShow, setData, closeText, generatedPassword, onHide }) => {
    const { t } = useTranslation(); // Translation hook for multilingual support
    const [buttonText, setButtonText] = useState("click_to_copy"); // Button label state
    const [copySecret, setCopySecret] = useState(false); // Tracks if the secret has been copied
    const [copyGeneratedPassword, setCopyGeneratedPassword] = useState(false); // Tracks if the password has been copied

    /**
     * Handles copying the secret key to the clipboard.
     * If already copied, hides the modal and triggers the onHide callback.
     */
    const handleConfirm = () => {
        if (copySecret) {
            setCopySecret(false);
            onHideModal();
            onHide();
            return;
        }

        const success = copy(secret);
        if(success){
            toast.success(t("text_copied_successfully"));
            setButtonText(closeText);
            setCopySecret(true);
        }else{
            toast.success(t("failed_to_copy_text"));
        }
    }

    /**
     * Handles copying both the secret key and the generated password to the clipboard.
     * If already copied, hides the modal and triggers the onHide callback.
     */
    const handleConfirmGeneratedPassword = () => {
        if (copyGeneratedPassword) {
            setCopyGeneratedPassword(false);
            onHideModal();
            onHide();
            return;
        }

        const success = copy(t("secret_key") +": "+ secret+ "\n"+ t("generated_password")+": "+ generatedPassword);
        if(success){
            toast.success(t("text_copied_successfully"));
            setButtonText(closeText);
            setCopyGeneratedPassword(true);
        }else{
            toast.success(t("failed_to_copy_text"));
        }
    }

    /**
     * Hides the modal and resets relevant state.
     */
    const onHideModal = () => {
        setShow(false);
        setSecret(null);
        setData(prev => ({ ...prev, password: '' }));
        setButtonText('click_to_copy');
    }

    // Return functions and states to be used by the component
    return {
        t,
        buttonText,
        handleConfirm,
        handleConfirmGeneratedPassword,
        onHideModal
    }
}

export default CopySecretApp;
